import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Camera,
  Image as ImageIcon,
  Loader2,
  X,
  Save,
} from 'lucide-react';
import { apiGet, apiUploadPatch } from '../shared/apiClient';
import type { Segnalazione } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { compressImages } from '../utils/imageCompress';
import { MAX_FOTO_SEGNALAZIONE, toggleMultiOpzione, isMultiOpzioneSelected } from '../shared/constants';
import { SecureImage } from './SecureImage';

interface ModificaSegnalazioneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  segnalazione: Segnalazione;
}

function parseFoto(fotoStr?: string | null): string[] {
  if (!fotoStr) return [];
  try {
    const parsed = typeof fotoStr === 'string' ? JSON.parse(fotoStr) : fotoStr;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function ModificaSegnalazioneModal({
  isOpen,
  onClose,
  onSuccess,
  segnalazione,
}: ModificaSegnalazioneModalProps) {
  const { toast } = useToast();

  // Opzioni anomalie dinamiche da /chiamate/liste-opzioni
  const [opzioniGuasto, setOpzioniGuasto] = useState<string[]>([
    'lampeggio',
    'spento',
    'lanterna urtata/girata',
    'palo a sbraccio girato',
    'centralino abbattuto',
    'pulsante guasto/rotto',
    'dispositivo non vedente guasto',
  ]);

  const [titolo, setTitolo] = useState(segnalazione.titolo || '');
  const [descrizione, setDescrizione] = useState(segnalazione.descrizione || '');
  const [fotoEsistenti, setFotoEsistenti] = useState<string[]>(parseFoto(segnalazione.foto));
  const [nuoveFotoFiles, setNuoveFotoFiles] = useState<File[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitolo(segnalazione.titolo || '');
    setDescrizione(segnalazione.descrizione || '');
    setFotoEsistenti(parseFoto(segnalazione.foto));
    setNuoveFotoFiles([]);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, segnalazione]);

  useEffect(() => {
    if (!isOpen) return;
    apiGet<{ guastiSegnalati?: string[] }>('/chiamate/liste-opzioni')
      .then((res) => {
        if (Array.isArray(res.data?.guastiSegnalati) && res.data.guastiSegnalati.length > 0) {
          setOpzioniGuasto(res.data.guastiSegnalati);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const totalFotoCount = fotoEsistenti.length + nuoveFotoFiles.length;

  const handleAddPhotos = async (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    setCompressing(true);
    try {
      const compressi = await compressImages(newFiles);
      const remainingSlots = MAX_FOTO_SEGNALAZIONE - fotoEsistenti.length;
      setNuoveFotoFiles((prev) => [...prev, ...compressi].slice(0, remainingSlots));
    } catch {
      const remainingSlots = MAX_FOTO_SEGNALAZIONE - fotoEsistenti.length;
      setNuoveFotoFiles((prev) => [...prev, ...newFiles].slice(0, remainingSlots));
    } finally {
      setCompressing(false);
    }
  };

  const handleRemoveExistingPhoto = (idx: number) => {
    setFotoEsistenti((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveNewPhoto = (idx: number) => {
    setNuoveFotoFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titolo.trim()) {
      toast({ type: 'error', message: 'Indica il tipo di guasto o anomalia riscontrata' });
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('titolo', titolo.trim());
      fd.append('descrizione', descrizione.trim());
      fd.append('fotoEsistenti', JSON.stringify(fotoEsistenti));
      for (const f of nuoveFotoFiles) {
        fd.append('nuoveFoto', f);
      }

      await apiUploadPatch(`/segnalazioni/${segnalazione.id}`, fd);
      toast({ type: 'success', message: 'Segnalazione aggiornata con successo' });
      onSuccess();
      onClose();
    } catch (err: any) {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Errore durante l\'aggiornamento della segnalazione' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const chipSelezionati = titolo
    ? titolo.split(/\s*-\s*/).map((s) => s.trim()).filter(Boolean)
    : [];

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/80 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full max-w-lg rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh] overflow-hidden animate-slide-up border border-slate-200">
        {/* Header fisso */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-extrabold text-slate-900 leading-tight">
                Modifica Segnalazione Guasto
              </h3>
              <p className="text-xs text-slate-500">
                Impianto [{segnalazione.impianto?.codice}] {segnalazione.impianto?.nome}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition active:scale-95"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form scrollabile */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {/* Tipo di Guasto / Anomalia */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Tipo di Guasto / Anomalia <span className="text-rose-500">*</span>
            </label>

            {/* Select a comparsa per scelte rapide */}
            <div className="relative">
              <select
                className="input text-xs font-medium bg-white cursor-pointer"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    setTitolo(toggleMultiOpzione(titolo, e.target.value));
                  }
                }}
              >
                <option value="">＋ Scegli anomalia frequente dal menu…</option>
                {opzioniGuasto.map((opz) => (
                  <option key={opz} value={opz}>
                    {isMultiOpzioneSelected(titolo, opz) ? `✓ ${opz}` : opz}
                  </option>
                ))}
              </select>
            </div>

            {/* Chip selezionati */}
            {chipSelezionati.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {chipSelezionati.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-100 text-amber-900 text-xs font-bold border border-amber-200"
                  >
                    <span>{c}</span>
                    <button
                      type="button"
                      onClick={() => setTitolo(toggleMultiOpzione(titolo, c))}
                      className="p-0.5 hover:bg-amber-200 rounded text-amber-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Input testuale sintetico */}
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Sintesi anomalia..."
              className="input text-xs font-medium mt-1"
            />
          </div>

          {/* Descrizione libera */}
          <div className="space-y-1">
            <label className="block text-xs font-bold text-slate-700">
              Descrizione dettagliata <span className="text-slate-400 font-normal">(opzionale)</span>
            </label>
            <textarea
              className="input min-h-[85px] resize-y text-xs font-medium"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Dettagli aggiuntivi..."
            />
          </div>

          {/* Foto (Esistenti + Nuove) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-700">
                Foto del disservizio / anomalia
              </label>
              <span className="text-[11px] font-semibold text-slate-500">
                {totalFotoCount}/{MAX_FOTO_SEGNALAZIONE} allegate
              </span>
            </div>

            {/* Griglia foto */}
            {(fotoEsistenti.length > 0 || nuoveFotoFiles.length > 0) && (
              <div className="grid grid-cols-5 gap-2">
                {/* Foto già caricate */}
                {fotoEsistenti.map((fn, idx) => (
                  <div key={`old-${idx}`} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                    <SecureImage
                      src={`/api/uploads/${fn}`}
                      alt="foto salvata"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingPhoto(idx)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white hover:bg-rose-600 transition"
                      title="Rimuovi foto"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {/* Nuove foto scelte */}
                {nuoveFotoFiles.map((file, idx) => (
                  <div key={`new-${idx}`} className="relative aspect-square rounded-xl overflow-hidden border border-emerald-300 bg-emerald-50">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="nuova anteprima"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveNewPhoto(idx)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white hover:bg-rose-600 transition"
                      title="Rimuovi nuova foto"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Pulsanti Fotocamera & Galleria se c'è spazio */}
            {totalFotoCount < MAX_FOTO_SEGNALAZIONE && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <label
                  className={`btn-outline py-2.5 px-3 flex items-center justify-center gap-2 cursor-pointer text-xs font-bold ${
                    compressing ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <Camera className="w-4 h-4 text-emerald-600" />
                  <span>Aggiungi Foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) handleAddPhotos(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                </label>

                <label
                  className={`btn-outline py-2.5 px-3 flex items-center justify-center gap-2 cursor-pointer text-xs font-bold ${
                    compressing ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <ImageIcon className="w-4 h-4 text-sky-600" />
                  <span>Galleria</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) handleAddPhotos(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}

            {compressing && (
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                <span>Compressione immagini in corso…</span>
              </div>
            )}
          </div>

          {/* Footer bottoni */}
          <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn-outline px-4 py-2.5 text-xs font-bold"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !titolo.trim()}
              className="btn-primary px-5 py-2.5 text-xs font-bold gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Salvataggio…</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Salva Modifiche</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
